import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { ka, enUS } from "date-fns/locale";

import EventIcon from "@assets/icons/event.svg";

import { MonthNavigation } from "@components/Helpers/Calendar/MonthNavigation";
import { DayNames } from "@components/Helpers/Calendar/DayNames";
import { CalendarDays } from "@components/Helpers/Calendar/CalendarDays";

import { Button } from "@components/Helpers/UIComponents";
import { daysOfWeekEng } from "@utils/sync/GeneralUtils";
import { getCalendarDays } from "@utils/sync/DateUtils";

import { useTranslations } from "next-intl";
import { useCookies } from "@Context/cookiesContext";
import Modal from "../Modal/Modal";
import MultiPurposeInput from "@components/MultiPurposeInput";

interface DateInputProps {
  selectedDate?: Date | null;
  setSelectedDate?: (date: Date | any) => void | any;
  allDaysSelectable?: boolean;
  classNameInput?: string;
  classNameContainer?: string;
  classNameIcon?: string;
  workingDays?: any;
  dateRange?: boolean; // New prop to indicate date range selection
  selectedDateRange?: { start: Date | null; end: Date | null }; // For range selection
  setSelectedDateRange?: (range: {
    start: Date | null;
    end: Date | null;
  }) => void; // Handler for range selection
  label?: string;
  isFilter?: boolean;
  resetValue?: () => void;
}

const DateInput: React.FC<DateInputProps> = ({
  selectedDate,
  setSelectedDate,
  selectedDateRange,
  setSelectedDateRange,
  dateRange,
  allDaysSelectable,
  classNameInput,
  classNameContainer,
  classNameIcon,
  workingDays,
  label,
  isFilter,
  resetValue,
}) => {
  const t = useTranslations("CustomInputs");
  const g = useTranslations("General");
  const cookies = useCookies();

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const formattedDate = selectedDate
    ? format(selectedDate, "dd MMMM, yyyy", {
        locale: cookies?.NEXT_LOCALE === "ka" ? ka : enUS,
      })
    : t("dateInputTitle");

  const formattedDateRange =
    selectedDateRange && selectedDateRange.start && selectedDateRange.end
      ? `${format(selectedDateRange.start, "dd MMMM", {
          locale: cookies?.NEXT_LOCALE === "ka" ? ka : enUS,
        })} - ${format(selectedDateRange.end, "dd MMMM", { locale: cookies?.NEXT_LOCALE === "ka" ? ka : enUS })}`
      : selectedDateRange && selectedDateRange.start
        ? `${format(selectedDateRange.start, "dd MMMM", { locale: cookies?.NEXT_LOCALE === "ka" ? ka : enUS })}`
        : label
          ? ""
          : t("dateInputDescription");

  // Determine which text to show (single date or date range)
  const displayText = dateRange ? formattedDateRange : formattedDate;

  const handleToggleModal = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate);
    }
  }, [selectedDate]);

  return (
    <>
      <MultiPurposeInput
        label={label ? label : t("dateInputTitle")}
        text={displayText}
        icon={<EventIcon />}
        isFilter={isFilter}
        isDropdown={true}
        isOpen={isOpen}
        onToggle={handleToggleModal}
        classNameInput={classNameInput}
        classNameContainer={classNameContainer}
        classNameIcon={classNameIcon}
        classNamePopup="overflow-y-auto"
        resetValue={resetValue}
      />
      <Modal
        title={t("dateInputDescription")}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <div className="picker-body overflow-y-scroll">
          <MonthNavigation
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            locale={cookies?.NEXT_LOCALE === "ka" ? ka : enUS}
          />
          <DayNames daysOfWeek={daysOfWeekEng} />
          <CalendarDays
            calendarDays={getCalendarDays(currentMonth)}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            currentMonth={currentMonth}
            allDaysSelectable={allDaysSelectable}
            workingDays={workingDays}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            dateRange={dateRange}
          />
          <Button
            type="button"
            onClick={handleToggleModal}
            className="primary_button flex"
          >
            {g("popupButtonChoose")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default DateInput;
